// Single source of truth for NAP + SEO content (consistent everywhere).
export const SITE = {
  name: "Let Go Wellness",
  domain: process.env.REACT_APP_SITE_DOMAIN || "https://letgowellnessseo.com",
  tagline: "Therapy in Waterbury, CT",
  street: "808 Highland Ave, 1st Fl",
  city: "Waterbury",
  state: "CT",
  zip: "06708",
  addressLine: "808 Highland Ave, 1st Fl, Waterbury, CT 06708",
  phoneOffice: "203-437-8193",
  phoneCell: "203-768-6969",
  email: "letgowellness1@gmail.com",
  googleReviewUrl: "https://g.page/r/CXQO2eGcoCpNEAE/review",
  geo: { lat: 41.5620, lng: -73.0631 },
  hours: [
    { d: "Mon – Fri", h: "8:00am – 8:00pm" },
    { d: "Saturday", h: "9:00am – 4:00pm" },
    { d: "Sunday", h: "Closed" },
  ],
  mapEmbed:
    "https://www.google.com/maps?q=808+Highland+Ave,+Waterbury,+CT+06708&output=embed",
  mapLink: "https://maps.google.com/?q=808%20Highland%20Ave%20Waterbury%20CT%2006708",
};

export const NAV = [
  { label: "Services", to: "/services/anxiety-stress-therapy-waterbury-ct", key: "services" },
  { label: "Our Approach", to: "/about" },
  { label: "Meet Waleska", to: "/therapists/waleska-castillo" },
  { label: "Insurance", to: "/insurance" },
  { label: "Journal", to: "/blog" },
  { label: "Visit", to: "/contact" },
  { label: "Admin", to: "/admin/login" },
];

export const HERO_IMAGE =
  "https://images.unsplash.com/photo-1746021451691-4385f318ec13?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
export const CLINIC_IMAGE =
  "https://images.unsplash.com/photo-1668672355709-a2f199a65201?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
export const THERAPIST_IMAGE = "/waleska-castillo.webp";

export const SERVICES = [
  {
    slug: "anxiety-stress-therapy-waterbury-ct",
    name: "Anxiety & Stress Therapy",
    short: "Anxiety & Stress",
    num: "01",
    image: "https://images.unsplash.com/photo-1661734421317-2afb3bc58013?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    title: "Anxiety & Stress Therapy in Waterbury, CT | Let Go Wellness",
    meta: "Evidence-based anxiety and stress therapy in Waterbury, CT. CBT, mindfulness & somatic care for a calmer mind. In-person & telehealth across Connecticut.",
    h1: "Anxiety & Stress Therapy in Waterbury, CT",
    tagline: "Calm the spinning mind — and stay calm.",
    intro:
      "If your thoughts race, your chest tightens, or you lie awake rehearsing tomorrow, you're not broken — your nervous system is working overtime. Anxiety therapy at our Waterbury clinic helps you understand what's driving the alarm and gives you practical, lasting tools to quiet it.",
    body: [
      "We blend cognitive behavioral therapy (CBT), acceptance and commitment therapy (ACT), and mindfulness-based, somatic approaches. That means we work with both the thoughts and the body that carries them.",
      "Whether it's generalized anxiety, panic, social anxiety, or health worry, we move at a pace that respects your system — never rushing, never forcing.",
    ],
    approaches: ["CBT & ACT", "Mindfulness-based stress reduction", "Somatic & breath work", "Panic & phobia support"],
    expect: "A first session to understand your patterns, then a gentle plan of tools you can actually use between visits.",
    faqs: [
      { q: "How long until I feel better?", a: "Many clients notice a shift within a few sessions, though lasting change is a gradual, personal process." },
      { q: "Do you offer telehealth for anxiety?", a: "Yes — secure video sessions are available across Connecticut if coming in feels like too much right now." },
    ],
  },
  {
    slug: "depression-therapy-waterbury-ct",
    name: "Depression Therapy",
    short: "Depression",
    num: "02",
    image: "https://images.unsplash.com/photo-1527137342181-19aab11a8ee8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    title: "Depression Therapy in Waterbury, CT | Let Go Wellness",
    meta: "Compassionate depression counseling in Waterbury, CT. Slowly return to color, energy and meaning with evidence-based, unhurried care. Telehealth across CT.",
    h1: "Depression & Low-Mood Therapy in Waterbury, CT",
    tagline: "Slowly return to color, at your own pace.",
    intro:
      "Depression can make ordinary things feel enormous and joyful things feel far away. You don't have to explain or justify it. Our role is to sit beside you and help you find footing again — one small, sustainable step at a time.",
    body: [
      "We use evidence-based approaches including CBT, behavioral activation, and IFS (Internal Family Systems), always paired with warmth and zero judgment.",
      "For many people, gentle, steady support alongside talk therapy helps re-awaken a sense of aliveness.",
    ],
    approaches: ["Behavioral activation", "CBT & IFS", "Grief-aware care", "Body-centred support"],
    expect: "A steady, low-pressure space with no prescribed timeline and no performance expected of you.",
    faqs: [
      { q: "What if I can barely get out of bed?", a: "That's exactly what we're here for. We start where you are, and telehealth means you don't have to leave home." },
      { q: "Do you coordinate with prescribers?", a: "Yes, with your consent we're glad to collaborate with your physician or psychiatrist." },
    ],
  },
  {
    slug: "couples-family-therapy-waterbury-ct",
    name: "Couples & Family Therapy",
    short: "Couples & Family",
    num: "03",
    image: "https://images.unsplash.com/photo-1604881991720-f91add269bed?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    title: "Couples & Family Therapy in Waterbury, CT | Let Go Wellness",
    meta: "Couples and family therapy in Waterbury, CT. Rebuild trust, communication and intimacy in a warm, neutral space. In-person & telehealth across Connecticut.",
    h1: "Couples & Family Therapy in Waterbury, CT",
    tagline: "Rebuild trust, communication and intimacy.",
    intro:
      "Every relationship has patterns — some that protect us, some that keep us stuck. Couples and family therapy isn't about assigning blame. It's about learning to hear each other again and choosing how you want to move forward, together.",
    body: [
      "We hold a warm, neutral space where each person feels heard. Using approaches drawn from Gottman and emotionally-focused therapy, we help you understand the cycle underneath the conflict.",
      "We support couples, co-parents, and families navigating change, communication breakdowns, life transitions, and rebuilding after rupture.",
    ],
    approaches: ["Emotionally-focused therapy", "Communication repair", "Co-parenting support", "Bilingual (English / Spanish)"],
    expect: "A first session together to map the pattern, then focused work on the changes you most want to see.",
    faqs: [
      { q: "What if only one of us wants to come?", a: "That's common. Individual work can create real change in a relationship, and the door stays open for your partner later." },
      { q: "Do you offer sessions in Spanish?", a: "Yes — our practitioner is fully bilingual in English and Spanish." },
    ],
  },
  {
    slug: "burnout-career-therapy-waterbury-ct",
    name: "Burnout & Career Therapy",
    short: "Burnout & Career",
    num: "04",
    image: "https://images.unsplash.com/photo-1522075782449-e45a34f1ddfb?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    title: "Burnout & Career Counseling in Waterbury, CT | Let Go Wellness",
    meta: "Burnout recovery and career counseling in Waterbury, CT. Recover from exhaustion and reshape your relationship with work. Telehealth across Connecticut.",
    h1: "Burnout & Career Therapy in Waterbury, CT",
    tagline: "Recover from exhaustion. Redefine enough.",
    intro:
      "Burnout isn't a personal failing — it's what happens when caring, capable people run on empty for too long. Together we'll help your nervous system recover and rebuild a relationship with work that doesn't cost you yourself.",
    body: [
      "We combine practical stress-recovery tools with deeper work on the beliefs — perfectionism, over-functioning, people-pleasing — that keep the cycle turning.",
      "For those who hold stress in the body, we bring in grounding, somatic tools to support genuine, physical rest.",
    ],
    approaches: ["Stress-recovery planning", "Boundary & values work", "Perfectionism & over-functioning", "Somatic rest support"],
    expect: "Room to exhale first, then a realistic plan to restore energy and protect it going forward.",
    faqs: [
      { q: "Is this just for professionals?", a: "Not at all — caregivers, parents, students and helpers of every kind experience burnout." },
      { q: "Can we meet outside work hours?", a: "Yes. We offer early-morning and evening slots, Monday through Friday until 8pm." },
    ],
  },
  {
    slug: "life-transitions-therapy-waterbury-ct",
    name: "Life Transitions Therapy",
    short: "Life Transitions",
    num: "05",
    image: "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    title: "Life Transitions & Grief Therapy in Waterbury, CT | Let Go Wellness",
    meta: "Support through grief, identity, parenthood and loss in Waterbury, CT. Steady counseling for the in-between chapters of life. Telehealth across Connecticut.",
    h1: "Life Transitions & Grief Therapy in Waterbury, CT",
    tagline: "For the in-between chapters.",
    intro:
      "Grief, a move, a diagnosis, parenthood, an ending, a beginning — transitions ask us to become someone slightly new. This is tender, disorienting work, and it's easier with a steady companion.",
    body: [
      "We hold space for the full range of what change brings: loss and relief, fear and hope, often all at once.",
      "Together we make meaning of what's shifting and help you find your footing on the other side.",
    ],
    approaches: ["Grief & loss", "Identity & purpose", "New-parent support", "Meaning-making"],
    expect: "A grounded, unhurried space to feel what's true and slowly re-orient toward what's next.",
    faqs: [
      { q: "Is grief only about death?", a: "No. We grieve relationships, roles, health, and versions of the future we expected. All of it is welcome here." },
      { q: "How long does this take?", a: "There's no correct timeline for transition or grief. We follow yours." },
    ],
  },
];

export const serviceBySlug = (slug) => SERVICES.find((s) => s.slug === slug);

export const TOWNS = [
  { slug: "therapist-naugatuck-ct", town: "Naugatuck", drive: "about 10 minutes south" },
  { slug: "therapist-cheshire-ct", town: "Cheshire", drive: "about 15 minutes east" },
  { slug: "therapist-southbury-ct", town: "Southbury", drive: "about 20 minutes west" },
  { slug: "therapist-wolcott-ct", town: "Wolcott", drive: "about 10 minutes northeast" },
  { slug: "therapist-watertown-ct", town: "Watertown", drive: "about 10 minutes northwest" },
  { slug: "therapist-middlebury-ct", town: "Middlebury", drive: "about 12 minutes west" },
  { slug: "therapist-prospect-ct", town: "Prospect", drive: "about 12 minutes southeast" },
];

export const townBySlug = (slug) => TOWNS.find((t) => t.slug === slug);

export const HOME_FAQS = [
  { q: "Do you take insurance?", a: "We accept most major plans including Aetna, Cigna, United/Oxford, Anthem/BCBS and more, and offer out-of-network and self-pay options. Share your provider when you enroll and we'll verify your benefits." },
  { q: "How quickly can I be seen?", a: "We respond to every enrollment within one business day and can often schedule a first session within the same week." },
  { q: "Do you offer telehealth?", a: "Yes — secure, HIPAA-compliant video sessions are available anywhere in Connecticut, alongside in-person visits at our Waterbury clinic." },
  { q: "What if it isn't a good fit?", a: "Fit matters more than anything. If we're not the right match, we'll say so and help you find someone who is." },
  { q: "Is what I share confidential?", a: "Absolutely. Everything you share is protected under HIPAA and held in strict confidence." },
  { q: "How long is each session?", a: "Individual sessions are 50 minutes, initial consultations 60 minutes, and couples/family sessions 75 minutes." },
];

export const INSURERS = [
  "Aetna",
  "Cigna",
  "UnitedHealthcare / Oxford",
  "Anthem Blue Cross Blue Shield",
  "Most major PPO plans",
  "Out-of-network & self-pay",
];

export const INSURANCE_FAQS = [
  { q: "Does my insurance cover therapy?", a: "Most major plans cover outpatient mental health therapy, often with just a copay after your deductible. We accept Aetna, Cigna, UnitedHealthcare/Oxford and Anthem/BCBS, and we verify your specific benefits before your first session so there are no surprises." },
  { q: "How do I find out what I'll pay?", a: "Share your insurance details when you enroll and we'll run a benefits check — confirming your copay, coinsurance and deductible — usually within one business day." },
  { q: "What if you're not in-network with my plan?", a: "Many clients use out-of-network benefits. We provide a monthly superbill you can submit to your insurer for partial reimbursement, and we offer transparent self-pay and sliding-scale options." },
  { q: "Do you offer self-pay or a sliding scale?", a: "Yes. If insurance isn't the right fit, we offer straightforward self-pay rates and a limited number of reduced sliding-scale spots. Ask us and we'll share current rates." },
  { q: "Is telehealth covered the same as in-person?", a: "In Connecticut, most plans cover telehealth therapy at the same rate as in-person visits. We offer secure video sessions anywhere in CT." },
  { q: "Will using insurance affect my privacy?", a: "Your care is confidential and HIPAA-protected. Insurers require a diagnosis for billing, but session content stays private between you and your therapist." },
];
